import type { HTMLAttributes, JSX, ReactNode } from 'react';

import styles from './FormLabel.module.css';

interface LabelProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'children'> {
  children: ReactNode;
  className?: string | null;
  /** Renders a muted "Optional" tag after the label text. */
  displayOptionalLabel?: boolean;
  htmlFor?: string;
  /** Renders a required marker (asterisk) after the label text. */
  required?: boolean;
}

export default function FormLabel({
  children,
  className,
  displayOptionalLabel,
  htmlFor,
  required,
  ...rest
}: LabelProps): JSX.Element {
  const Tag = htmlFor ? 'label' : 'div';
  // Compose with the base style instead of replacing it, and put the spread
  // BEFORE className so a caller's attributes can't knock the base class off.
  // (Spreading after it was how this prop got silently dropped.)
  const classes = className ? `${styles.Label} ${className}` : styles.Label;

  return (
    <Tag {...rest} htmlFor={htmlFor} className={classes}>
      {children}
      {required && (
        <span className={styles.Required}>
          <span aria-hidden="true">*</span>
          <span className={styles.SrOnly}>(required)</span>
        </span>
      )}
      {displayOptionalLabel && <span className={styles.Optional}>Optional</span>}
    </Tag>
  );
}
